import React, { useState, useEffect } from 'react';
import { calendarAPI, usersAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, User, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_TYPES = [
  { value: 'publish', label: 'Publish', color: '#1F7A5A' },
  { value: 'meeting', label: 'Meeting', color: '#1E5A8A' },
  { value: 'deadline', label: 'Deadline', color: '#B3261E' },
  { value: 'assignment', label: 'Assignment', color: '#B89B5E' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export default function AdminCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', event_type: 'publish', date: '', assigned_to: '', priority: 'medium' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [month, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [evRes, staffRes] = await Promise.all([
        calendarAPI.list({ month: month + 1, year }),
        usersAPI.list({ limit: 100 }).catch(() => ({ data: { users: [] } }))
      ]);
      setEvents(evRes.data.events || []);
      setStaff(staffRes.data.users || []);
    } catch { toast.error('Failed to load calendar'); }
    finally { setLoading(false); }
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date?.startsWith(dateStr));
  };

  const handleSave = async () => {
    if (!form.title || !form.date) { toast.error('Title and date are required'); return; }
    try {
      if (editingEvent) {
        await calendarAPI.update(editingEvent.id, form);
        toast.success('Event updated');
      } else {
        await calendarAPI.create(form);
        toast.success('Event created');
      }
      setShowDialog(false);
      setEditingEvent(null);
      setForm({ title: '', description: '', event_type: 'publish', date: '', assigned_to: '', priority: 'medium' });
      loadData();
    } catch { toast.error('Failed to save event'); }
  };

  const handleDelete = async (id) => {
    try {
      await calendarAPI.delete(id);
      toast.success('Event deleted');
      loadData();
    } catch { toast.error('Failed to delete event'); }
  };

  const openCreate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T09:00:00`;
    setForm({ title: '', description: '', event_type: 'publish', date: dateStr, assigned_to: '', priority: 'medium' });
    setEditingEvent(null);
    setShowDialog(true);
  };

  const openEdit = (event) => {
    setForm({ title: event.title, description: event.description || '', event_type: event.event_type, date: event.date, assigned_to: event.assigned_to || '', priority: event.priority || 'medium' });
    setEditingEvent(event);
    setShowDialog(true);
  };

  const days = [];
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6" data-testid="admin-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="w-6 h-6 text-[#B89B5E]" /> Editorial Calendar</h1>
          <p className="text-sm text-[#5B616E] mt-1">Plan content, assignments, and deadlines</p>
        </div>
        <Button onClick={() => { setForm({ title: '', description: '', event_type: 'publish', date: new Date().toISOString().slice(0, 16), assigned_to: '', priority: 'medium' }); setEditingEvent(null); setShowDialog(true); }} data-testid="create-event-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Plus className="w-4 h-4 mr-2" /> New Event
        </Button>
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
          <CardTitle className="text-lg news-masthead">{monthName} {year}</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {weekdays.map(d => <div key={d} className="text-center text-xs font-semibold text-[#5B616E] py-2">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} className="h-24 rounded-lg bg-[#FBF7EF]/50" />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dayEvents = getEventsForDay(day);
                  const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
                  return (
                    <div key={day} className={`h-24 rounded-lg border p-1 cursor-pointer hover:bg-[#FBF7EF] transition-colors overflow-hidden ${isToday ? 'border-[#B89B5E] bg-[#B89B5E]/5' : 'border-[#E7E0D6]'}`} onClick={() => openCreate(day)}>
                      <span className={`text-xs font-medium ${isToday ? 'text-[#B89B5E]' : 'text-[#5B616E]'}`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {dayEvents.slice(0, 3).map(ev => {
                          const typeColor = EVENT_TYPES.find(t => t.value === ev.event_type)?.color || '#5B616E';
                          return (
                            <div key={ev.id} className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer" style={{ background: `${typeColor}15`, color: typeColor }} onClick={(e) => { e.stopPropagation(); openEdit(ev); }}>
                              {ev.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && <span className="text-[10px] text-[#5B616E]">+{dayEvents.length - 3} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm font-medium">Upcoming Events</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {events.slice(0, 10).map(ev => {
              const typeInfo = EVENT_TYPES.find(t => t.value === ev.event_type);
              return (
                <div key={ev.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#FBF7EF] transition-colors group">
                  <div className="w-1 h-10 rounded-full" style={{ background: typeInfo?.color || '#5B616E' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ev.title}</p>
                    <div className="flex items-center gap-2 text-xs text-[#5B616E] mt-0.5">
                      <Clock className="w-3 h-3" />
                      <span>{ev.date ? new Date(ev.date).toLocaleDateString() : ''}</span>
                      {ev.assigned_to_name && <><User className="w-3 h-3 ml-1" /><span>{ev.assigned_to_name}</span></>}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{typeInfo?.label || ev.event_type}</Badge>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(ev)}><Edit2 className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => handleDelete(ev.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              );
            })}
            {events.length === 0 && <p className="text-sm text-[#5B616E] text-center py-8">No events scheduled for this month</p>}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'New Calendar Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title</label>
              <Input data-testid="event-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Event title" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Date & Time</label>
              <Input type="datetime-local" value={form.date?.slice(0, 16) || ''} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Type</label>
                <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Assign To</label>
              <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-event-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editingEvent ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
